import { ReactNode, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik, useFormikContext } from "formik";
import { useDebouncedCallback } from "use-debounce";
import useQueryParams from "../Fetch/useQueryParams.tsx";
import { _QueryTable } from "./QueryTable.tsx";
import { usePaginationState } from "./Pagination.tsx";
import { useSortBy } from "./Table.tsx";
import styles from "./Table.module.css";

const useHistoryPush = (
  filterByState,
  sortByState,
  paginationState,
  filterValuesToVariable,
) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [, setFilterBy] = filterByState;
  const [sortBy] = sortByState;
  const [pagination, setPagination] = paginationState;

  const historyPush = (values) => {
    const f = filterValuesToVariable(values);
    const filter = Object.values(f).filter(Boolean).length
      ? JSON.stringify(f)
      : null;
    const sort = Object.values(sortBy).filter(Boolean).length
      ? JSON.stringify(sortBy)
      : null;
    const searchParams = new URLSearchParams(document.location?.search);
    if (
      filter !== searchParams.get("filterBy") ||
      sort !== searchParams.get("sortBy")
    ) {
      if (filter) {
        searchParams.set("filterBy", filter);
      } else {
        searchParams.delete("filterBy");
      }
      if (sort) {
        searchParams.set("sortBy", sort);
      } else {
        searchParams.delete("sortBy");
      }
      searchParams.set("limit", pagination.limit);
      searchParams.set("offset", pagination.offset ?? "0");
      navigate({
        pathname: location.pathname,
        search: decodeURIComponent(searchParams.toString()),
      });
      setFilterBy(filterValuesToVariable(values));

      // Si on change de filtres, on se remet à la première page
      setPagination({ offset: 0, limit: pagination.limit });
    } else if (
      searchParams.get("offset") !== pagination.offset.toString() ||
      searchParams.get("limit") !== pagination.limit.toString()
    ) {
      searchParams.set("limit", pagination.limit);
      searchParams.set("offset", pagination.offset ?? "0");
      navigate(
        {
          pathname: location.pathname,
          search: decodeURIComponent(searchParams.toString()),
        },
        { replace: true },
      );
    }
  };

  return [historyPush];
};

export const _QueryTableWithFilter = ({
  filterComponent,
  filterValuesToVariable,
  watchedValues = [],
  initializer,
  filterByState,
  sortByState,
  paginationState,
  historyPush,
  isAutoSubmit,
}: _QueryTableWithFilter) => {
  const location = useLocation();
  const { values, setValues } = useFormikContext();

  const [, setFilterBy] = filterByState;
  const [sortBy, setSortBy] = sortByState;
  const [pagination, setPagination] = paginationState;

  const { callback: debounceSearch } = useDebouncedCallback(
    () => historyPush(values),
    500,
  );

  useEffect(() => {
    if (isAutoSubmit) {
      debounceSearch();
    }
  }, [isAutoSubmit, values, debounceSearch]);

  useEffect(() => {
    historyPush(values);
  }, [sortBy, pagination.offset, pagination.limit, ...watchedValues]);

  useEffect(() => {
    const searchParams = new URLSearchParams(document.location?.search);
    const filterByParams = searchParams.get("filterBy");
    const sortByParams = searchParams.get("sortBy");
    const offsetParams = searchParams.get("offset");
    const limitParams = searchParams.get("limit");

    if (filterByParams || (!filterByParams && values)) {
      const filteredValues = filterValuesToVariable(values);
      const filter = Object.values(filteredValues).filter(Boolean).length
        ? JSON.stringify(filteredValues)
        : null;
      if (filter !== filterByParams) {
        initializer && setValues(initializer(JSON.parse(filterByParams) ?? {}));
        setFilterBy(filteredValues);
      }
    }
    if (sortByParams || (!sortByParams && sortBy)) {
      const sort = Object.values(sortBy).filter(Boolean).length
        ? JSON.stringify(sortBy)
        : null;
      if (sort !== sortByParams) {
        setSortBy(JSON.parse(sortByParams) ?? {});
      }
    }
    if (
      offsetParams &&
      limitParams &&
      (offsetParams !== pagination.offset.toString() ||
        limitParams !== pagination.limit.toString())
    ) {
      setPagination({ offset: offsetParams, limit: limitParams });
    }
  }, [location]);

  return <>{filterComponent}</>;
};

type _QueryTableWithFilter = {
  filterValuesToVariable: (values: any) => object;
  filterComponent: ReactNode;
  watchedValues: string[];
  initializer: (values: any) => object;
} & _QueryTable;

const QueryTableWithFilter = ({
  initialFilterValues,
  initializer = (data) => data,
  filterValuesToVariable,
  watchedValues = [],
  trClassName,
  nomPage,
  filterComponent,
  titreSticky,
  stickyRight,
  isAutoSubmit = true,
  ...props
}: QueryTableWithFilterType) => {
  const initialValues = {
    ...initialFilterValues,
    ...initializer(useQueryParams()["filterBy"] ?? {}),
  };
  const [filterBy, setFilterBy] = useState(
    filterValuesToVariable(initialValues),
  );
  const [sortBy, setSortBy] = useSortBy();
  const [pagination, setPagination] = usePaginationState();

  const [historyPush] = useHistoryPush(
    [filterBy, setFilterBy],
    [sortBy, setSortBy],
    [pagination, setPagination],
    filterValuesToVariable,
  );

  return (
    <div className={styles.queryTableFilterWrapper}>
      <Formik
        initialValues={initialValues}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={(values) => {
          historyPush(values);
        }}
      >
        <>
          <_QueryTableWithFilter
            {...props}
            filterByState={[filterBy, setFilterBy]}
            sortByState={[sortBy, setSortBy]}
            paginationState={[pagination, setPagination]}
            filterValuesToVariable={filterValuesToVariable}
            watchedValues={watchedValues}
            initializer={(values) => {
              return { ...initialFilterValues, ...initializer(values) };
            }}
            filterComponent={filterComponent}
            historyPush={historyPush}
            isAutoSubmit={isAutoSubmit}
          />
          <_QueryTable
            filterBy={filterBy}
            sortByState={[sortBy, setSortBy]}
            paginationState={[pagination, setPagination]}
            trClassName={trClassName}
            nomPage={nomPage}
            titreSticky={titreSticky}
            stickyRight={
              stickyRight === "Filter" ? filterComponent : stickyRight
            }
            {...props}
          />
        </>
      </Formik>
    </div>
  );
};
type QueryTableWithFilterType = {
  initialFilterValues: object;
  filterValuesToVariable: (values: any) => object;
  initializer: (values: any) => object;
  watchedValues: string[];
} & _QueryTableWithFilter;

export default QueryTableWithFilter;
